import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X,
  Hash,
  Plus,
  Check,
  Loader2,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { agentChannelsApi, type AgentChannel } from "@/api/agent-channels";
import { chatApi, type ChatSession } from "@/api/chat";

interface MobileChannelSheetProps {
  open: boolean;
  onClose: () => void;
  agentId: string | null;
  activeChannelId: string | null;
  /** Currently-open session, if any — used to show a checkmark in the list. */
  activeSessionId?: string | null;
}

const CHANNEL_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f59e0b",
  "#10a37f", "#3b82f6", "#ef4444", "#14b8a6",
];

interface ChannelLike {
  id: string | "general";
  name: string;
  color: string;
}

function relTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

export function MobileChannelSheet({
  open,
  onClose,
  agentId,
  activeChannelId,
  activeSessionId,
}: MobileChannelSheetProps) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(CHANNEL_COLORS[0]);

  // Two-pane navigation inside the same sheet: channel list → sessions list
  const [view, setView] = useState<"channels" | "sessions">("channels");
  const [drilledChannel, setDrilledChannel] = useState<ChannelLike | null>(null);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Reset transient state whenever the sheet closes
  useEffect(() => {
    if (!open) {
      setAdding(false);
      setNewName("");
      setView("channels");
      setDrilledChannel(null);
    }
  }, [open]);

  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", agentId],
    queryFn: () => agentChannelsApi.list(agentId!),
    enabled: open && !!agentId,
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["chat-sessions", agentId],
    queryFn: () => chatApi.listSessions(agentId!),
    enabled: open && !!agentId,
    refetchInterval: (query) =>
      query.state.data?.some((session) => session.isGenerating) ? 2000 : 8000,
    refetchOnWindowFocus: true,
  });

  const sessionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    let general = 0;
    for (const s of sessions) {
      if (s.channelId) counts.set(s.channelId, (counts.get(s.channelId) || 0) + 1);
      else general += 1;
    }
    return { counts, general };
  }, [sessions]);

  const drilledSessions = useMemo(() => {
    if (!drilledChannel) return [] as ChatSession[];
    if (drilledChannel.id === "general") return sessions.filter((s) => !s.channelId);
    return sessions.filter((s) => s.channelId === drilledChannel.id);
  }, [drilledChannel, sessions]);

  const createChannel = useMutation({
    mutationFn: () =>
      agentChannelsApi.create(agentId!, {
        name: newName.trim(),
        color: newColor,
        shared: false,
      }),
    onSuccess: (newCh) => {
      queryClient.invalidateQueries({ queryKey: ["agent-channels", agentId] });
      setAdding(false);
      setNewName("");
      // Drill straight into the new (empty) channel so the user can start
      // their first chat there.
      setDrilledChannel({ id: newCh.id, name: newCh.name, color: newCh.color || "#8b5cf6" });
      setView("sessions");
    },
  });

  const createSessionInChannel = useMutation({
    mutationFn: () => {
      if (!agentId || !drilledChannel) {
        return Promise.reject(new Error("No agent/channel selected"));
      }
      const ch = drilledChannel.id === "general" ? undefined : drilledChannel.id;
      return chatApi.createSession(tenantId, agentId, ch);
    },
    onSuccess: (session) => {
      queryClient.setQueryData<ChatSession[]>(
        ["chat-sessions", agentId],
        (prev) => (prev ? [session, ...prev] : [session]),
      );
      openSession(session.id);
    },
  });

  /** Navigate straight into a session — closes the sheet on success. */
  const openSession = (sessionId: string) => {
    if (!agentId || !drilledChannel) return;
    const params = new URLSearchParams();
    params.set("agentId", agentId);
    if (drilledChannel.id !== "general") params.set("channelId", drilledChannel.id);
    params.set("sessionId", sessionId);
    navigate(`/tenants/${tenantId}/workspace?${params.toString()}`);
    onClose();
  };

  const drillInto = (ch: ChannelLike) => {
    setDrilledChannel(ch);
    setView("sessions");
  };

  const backToChannels = () => {
    setView("channels");
    setDrilledChannel(null);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 sm:hidden"
        onClick={onClose}
      />
      <div className="glass-raised fixed inset-x-0 bottom-0 z-50 sm:hidden max-h-[80vh] flex flex-col rounded-t-[28px] border-b-0 animate-in slide-in-from-bottom">
        <div className="mg-grabber" aria-hidden="true" />
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            {view === "sessions" ? (
              <button
                onClick={backToChannels}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-surface-hover text-text-primary"
                aria-label="Back to channels"
              >
                <ChevronLeft size={18} />
              </button>
            ) : (
              <span className="flex h-8 w-8 items-center justify-center">
                <Hash size={16} className="text-accent" />
              </span>
            )}
            <span className="text-sm font-semibold text-text-primary">
              {view === "channels"
                ? "Channels"
                : drilledChannel?.name ?? "Sessions"}
            </span>
            {view === "sessions" && drilledChannel && (
              <span
                className="inline-block h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: drilledChannel.color }}
              />
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          {view === "channels"
            ? renderChannelList({
                channels,
                activeChannelId,
                generalCount: sessionCounts.general,
                namedCounts: sessionCounts.counts,
                drillInto,
                adding,
                setAdding,
                newName,
                setNewName,
                newColor,
                setNewColor,
                createChannel,
              })
            : renderSessionList({
                sessions: drilledSessions,
                activeSessionId: activeSessionId ?? null,
                openSession,
                creating: createSessionInChannel.isPending,
                onNewSession: () => createSessionInChannel.mutate(),
                channelName: drilledChannel?.name ?? "",
              })}
        </div>
      </div>
    </>
  );
}

// ─── Channel list pane ─────────────────────────────────────────
function renderChannelList(args: {
  channels: AgentChannel[];
  activeChannelId: string | null;
  generalCount: number;
  namedCounts: Map<string, number>;
  drillInto: (ch: ChannelLike) => void;
  adding: boolean;
  setAdding: (v: boolean) => void;
  newName: string;
  setNewName: (v: string) => void;
  newColor: string;
  setNewColor: (v: string) => void;
  createChannel: ReturnType<typeof useMutation<AgentChannel, Error, void, unknown>>;
}) {
  const {
    channels,
    activeChannelId,
    generalCount,
    namedCounts,
    drillInto,
    adding,
    setAdding,
    newName,
    setNewName,
    newColor,
    setNewColor,
    createChannel,
  } = args;

  return (
    <>
      <ChannelRow
        color="#9CA3AF"
        name="General"
        count={generalCount}
        active={activeChannelId === "general" || !activeChannelId}
        onClick={() => drillInto({ id: "general", name: "General", color: "#9CA3AF" })}
      />

      {channels.map((ch) => (
        <ChannelRow
          key={ch.id}
          color={ch.color || "#8b5cf6"}
          name={ch.name}
          iconEmoji={ch.iconEmoji ?? undefined}
          count={namedCounts.get(ch.id) || 0}
          active={activeChannelId === ch.id}
          onClick={() => drillInto({ id: ch.id, name: ch.name, color: ch.color || "#8b5cf6" })}
        />
      ))}

      {!adding ? (
        <button
          onClick={() => setAdding(true)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left text-accent hover:bg-surface-hover"
        >
          <Plus size={16} className="shrink-0" />
          <span className="text-sm font-medium">Add new channel</span>
        </button>
      ) : (
        <div className="p-4 border-t border-border bg-surface/30">
          <label className="mb-1.5 block text-[11px] font-medium text-text-muted uppercase tracking-wide">
            Channel name
          </label>
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. bugs, ideas, standup"
            className="glass-field w-full rounded-[13px] px-3 py-2.5 text-sm text-text-primary outline-none placeholder:text-text-muted"
          />
          <label className="mb-1.5 mt-3 block text-[11px] font-medium text-text-muted uppercase tracking-wide">
            Colour
          </label>
          <div className="flex flex-wrap gap-2">
            {CHANNEL_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={cn(
                  "h-7 w-7 rounded-full border-2 transition-transform",
                  newColor === c ? "border-text-primary scale-110" : "border-transparent",
                )}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => { setAdding(false); setNewName(""); }}
              className="glass-field flex-1 rounded-full py-2 text-sm font-medium text-text-secondary"
            >
              Cancel
            </button>
            <button
              disabled={!newName.trim() || createChannel.isPending}
              onClick={() => createChannel.mutate()}
              className="mg-accent-fill flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-semibold disabled:opacity-50"
            >
              {createChannel.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              Create
            </button>
          </div>
          {createChannel.isError && (
            <p className="mt-2 text-xs text-red-500">
              {(createChannel.error as Error).message || "Failed to create channel"}
            </p>
          )}
        </div>
      )}
    </>
  );
}

function ChannelRow({
  color,
  name,
  iconEmoji,
  count,
  active,
  onClick,
}: {
  color: string;
  name: string;
  iconEmoji?: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors border-b border-border/50",
        active ? "bg-accent/10" : "hover:bg-surface-hover",
      )}
    >
      <span
        className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="flex-1 text-sm font-medium text-text-primary truncate">
        {iconEmoji ? `${iconEmoji} ` : ""}
        {name}
      </span>
      <span className="text-[11px] text-text-muted">
        {count === 0 ? "No chats" : `${count} chat${count === 1 ? "" : "s"}`}
      </span>
      <ChevronRight size={14} className="text-text-muted shrink-0" />
    </button>
  );
}

// ─── Session list pane ─────────────────────────────────────────
function renderSessionList(args: {
  sessions: ChatSession[];
  activeSessionId: string | null;
  openSession: (id: string) => void;
  creating: boolean;
  onNewSession: () => void;
  channelName: string;
}) {
  const { sessions, activeSessionId, openSession, creating, onNewSession, channelName } = args;
  const sorted = [...sessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  return (
    <>
      <button
        disabled={creating}
        onClick={onNewSession}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-accent hover:bg-surface-hover disabled:opacity-60"
      >
        {creating ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Plus size={16} />
        )}
        <span className="text-sm font-medium">
          {creating ? "Starting…" : `New chat in #${channelName}`}
        </span>
      </button>

      {sorted.length === 0 && !creating ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <MessageSquare size={22} className="text-text-muted" />
          <p className="text-[13px] text-text-muted">
            No chats in #{channelName} yet. Start one above.
          </p>
        </div>
      ) : (
        sorted.map((s) => {
          const active = s.id === activeSessionId;
          const showUnread = s.hasUnread && !active && !s.isGenerating;
          return (
            <button
              key={s.id}
              onClick={() => openSession(s.id)}
              aria-label={`${s.title || "Untitled chat"}${s.isGenerating ? ", response in progress" : showUnread ? ", new response" : ""}`}
              className={cn(
                "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors border-b border-border/50",
                active ? "bg-accent/10" : "hover:bg-surface-hover",
              )}
            >
              <MessageSquare
                size={16}
                className={cn("mt-0.5 shrink-0", active ? "text-accent" : "text-text-muted")}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-medium text-text-primary">
                  {s.title || "Untitled chat"}
                </div>
                <div className="text-[11px] text-text-muted">
                  Updated {relTime(s.updatedAt)}
                </div>
              </div>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                {s.isGenerating ? (
                  <Loader2 size={14} className="animate-spin text-blue-500" aria-hidden="true" />
                ) : showUnread ? (
                  <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true" />
                ) : active ? (
                  <Check size={14} className="text-accent" />
                ) : null}
              </span>
            </button>
          );
        })
      )}
    </>
  );
}
